import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import config from '../../../config';
import { apiFetch } from '../../_services/apiFetch';
import DatacoLoadingIndicator from '../../_components/DatacoLoadingIndicator';
import { buildWaitingForInspectionRows } from './waitingForInspectionUtils';

const recordTypes = [
    { value: 'all', label: 'All' },
    { value: 'fpbo', label: 'FPBO' },
    { value: 'lpbo', label: 'LPBO' },
    { value: 'fai', label: 'FAI' }
];

const getAccountId = () => {
    try {
        const user = JSON.parse(localStorage.getItem('user'));
        return user && user.accountId;
    } catch (error) {
        return null;
    }
};

function WaitingForInspection() {
    const navigate = useNavigate();
    const [rows, setRows] = useState([]);
    const [recordType, setRecordType] = useState('all');
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState('');
    const requestRef = useRef(null);
    const hasRowsRef = useRef(false);
    const mountedRef = useRef(true);
    const accountId = getAccountId();

    const loadRows = useCallback(() => {
        if (requestRef.current) return requestRef.current;

        if (!accountId) {
            setLoading(false);
            setError('Unable to load waiting-for-inspection records.');
            return Promise.resolve();
        }

        setError('');
        if (hasRowsRef.current) {
            setRefreshing(true);
        } else {
            setLoading(true);
        }

        const endpoints = [
            ['fpbo', `${config.apiUrl}/getFpboSummaryByCompany?company=${encodeURIComponent(accountId)}&limit=10000&offset=0`],
            ['lpbo', `${config.apiUrl}/getLpboSummaryByCompany?company=${encodeURIComponent(accountId)}&limit=10000&offset=0`],
            ['fai', `${config.apiUrl}/getFaiSummaryByCompany?company=${encodeURIComponent(accountId)}&limit=10000&offset=0`]
        ];

        const request = Promise.all(endpoints.map(async ([type, url]) => {
            const response = await apiFetch(url, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' }
            });
            if (!response.ok) throw new Error(`Unable to load ${type.toUpperCase()} records.`);

            const body = await response.json();
            return [type, Array.isArray(body.data) ? body.data : []];
        }))
            .then((responses) => {
                if (!mountedRef.current) return;

                const recordsByType = Object.fromEntries(responses);
                const nextRows = buildWaitingForInspectionRows(recordsByType);
                hasRowsRef.current = nextRows.length > 0;
                setRows(nextRows);
            })
            .catch(() => {
                if (mountedRef.current) {
                    setError('Unable to load waiting-for-inspection records.');
                }
            })
            .finally(() => {
                if (requestRef.current === request) {
                    requestRef.current = null;
                }
                if (mountedRef.current) {
                    setLoading(false);
                    setRefreshing(false);
                }
            });

        requestRef.current = request;
        return request;
    }, [accountId]);

    useEffect(() => {
        loadRows();

        return () => {
            mountedRef.current = false;
        };
    }, [loadRows]);

    const filteredRows = useMemo(() => {
        if (recordType === 'all') return rows;
        return rows.filter((row) => row.type.toLowerCase() === recordType);
    }, [recordType, rows]);

    if (loading) {
        return <DatacoLoadingIndicator loading size="compact" label="Loading waiting-for-inspection records" />;
    }

    return (
        <div className="container">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-3">
                <h4 className="mb-0">Waiting for Inspection</h4>
                <div className="d-flex align-items-center gap-2">
                    <label htmlFor="waiting-for-inspection-type" className="mb-0">Record type</label>
                    <select
                        id="waiting-for-inspection-type"
                        aria-label="Record type"
                        className="form-select"
                        value={recordType}
                        onChange={(event) => setRecordType(event.target.value)}
                    >
                        {recordTypes.map((type) => <option key={type.value} value={type.value}>{type.label}</option>)}
                    </select>
                </div>
            </div>

            {error && (
                <div className="alert alert-danger d-flex justify-content-between align-items-center" role="alert">
                    <span>{error}</span>
                    <button type="button" className="btn btn-outline-danger btn-sm" onClick={loadRows}>Retry</button>
                </div>
            )}

            {refreshing && <div className="text-muted mb-2">Refreshing records…</div>}

            {!error && filteredRows.length === 0 && (
                <div className="alert alert-info" role="status">No records are waiting for inspection.</div>
            )}

            {!error && filteredRows.length > 0 && (
                <div className="table-responsive">
                    <table className="table table-striped table-hover align-middle">
                        <thead>
                            <tr>
                                <th>Report No.</th>
                                <th>Part No.</th>
                                <th>Type</th>
                                <th>Description</th>
                                <th>Job No.</th>
                                <th>Submitted At</th>
                                <th>View Details</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredRows.map((row) => (
                                <tr key={row.type + '-' + row.id}>
                                    <td>{row.reportNo}</td>
                                    <td>{row.partNo}</td>
                                    <td>{row.type}</td>
                                    <td>{row.description}</td>
                                    <td>{row.jobNo}</td>
                                    <td>{row.submittedAt}</td>
                                    <td>
                                        <button type="button" className="btn btn-primary btn-sm" onClick={() => navigate(row.detailPath)}>
                                            View Details
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

export default WaitingForInspection;
